import { formatAddress, getFolderBreadcrumbs, getFolderParents } from '../../utils/helpers'

export const formatAccounts = (accounts: ISearchResultAccountApi[], tree: IFolderTree): ISearchResultAccount[] => {
  const findParent = (parentFolders: { id: number; name: string; folderType: FolderType }[]) => {
    // climb parentFolders array to find the first parent that is a Program first, Client Company second, or return the last parent
    let parent = parentFolders.find((pf) => pf.folderType === 'Program')
    if (!parent) parent = parentFolders.find((pf) => pf.folderType === 'Client Company')
    if (!parent) parent = parentFolders[parentFolders.length - 1]
    return parent
  }

  const formattedAccounts = accounts.map((account) => {
    const { address, clientAccountNumber, pathArr, parentFolders, id, accountStatus, name, folderType, isNonProduction, score, tenantId } = account
    const formattedAddress = address ? formatAddress(address, address.country) : ''
    const hierarchy = pathArr.slice(0, pathArr.length - 1)

    return {
      address: formattedAddress,
      accountNumber: clientAccountNumber,
      parents: getFolderBreadcrumbs({ tree, hierarchy, divider: ' / ' }),
      hierarchy,
      parentName: findParent(parentFolders)?.name,
      id,
      accountStatus,
      name,
      folderType,
      isNonProduction: !!isNonProduction,
      entityType: 'accounts' as const,
      isDeactivated: !!name.toLowerCase().includes('opt out') || name.toLowerCase().includes('opt-out'),
      score,
      tenantId,
    }
  })
  return formattedAccounts
}

export const formatDevices = (devices: ISearchResultDeviceApi[], tree: IFolderTree): ISearchResultDevice[] => {
  const formattedDevices = devices.map((d) => {
    const { address, folderId, id, uniqueId, vanity, isActive, properties: apiProperties, score, tenantId } = d
    const isDeactivated = !isActive
    const isAmmonia = apiProperties.includes('ammonia')
    const isBVS = apiProperties.includes('bvs')
    const isGateway = apiProperties.includes('gateway')
    const isLora = apiProperties.includes('lora')
    const isSensor = apiProperties.includes('sensor')
    const isTing = apiProperties.includes('ting')
    const isV3 = apiProperties.includes('v3')

    const formattedAddress = address ? formatAddress(address, address.country) : ''
    const properties = []
    if (isAmmonia) properties.push('Ammonia')
    if (isBVS) properties.push('Water Shutoff')
    if (isGateway) properties.push('Gateway')
    if (isLora) properties.push('LoRa')
    if (isSensor) properties.push('Sensor')
    if (isTing) properties.push('Ting')
    if (isV3) properties.push('V3')
    if (isSensor && !isAmmonia && !isBVS && !isLora && !isTing) properties.push('Leak & Freeze')

    const hierarchy = [...getFolderParents(folderId, tree), folderId]

    return {
      id,
      uniqueId,
      address: formattedAddress,
      folderName: tree[folderId] ? tree[folderId].name : '',
      parents: getFolderBreadcrumbs({ tree, hierarchy, divider: ' / ' }),
      vanity,
      isDeactivated,
      properties,
      entityType: 'devices' as const,
      score,
      tenantId,
    }
  })
  return formattedDevices
}

export const formatPeople = (people: ISearchResultPersonApi[]): ISearchResultPerson[] => {
  const formattedPeople = people.map((p) => {
    const { id, firstName, lastName, email, phone, roleName, folderIds, hasPassword, topAssignedFolders, score, tenantId } = p
    return {
      id,
      name: `${firstName} ${lastName}`,
      email,
      phoneNumber: phone,
      parents: topAssignedFolders[0]?.name,
      roleName,
      isDeactivated: !hasPassword,
      entityType: 'people' as const,
      folderIds,
      score,
      tenantId,
    }
  })
  return formattedPeople
}

export const formatOrders = (orders: ISearchResultOrderApi[], tree: IFolderTree): ISearchResultOrder[] => {
  const formattedOrders = orders.map((o) => {
    const {
      contactAddress,
      contactName,
      contactEmail,
      contactPhoneNumber,
      folderId,
      clientAccountNumber,
      id,
      shortOrderNumber,
      orderStatus,
      trackingNumber,
      orderType,
      score,
      tenantId,
    } = o
    const hierarchy = [...getFolderParents(folderId, tree), folderId]
    const formattedAddress = formatAddress(contactAddress, contactAddress.country)
    return {
      id,
      shortOrderNumber,
      parents: getFolderBreadcrumbs({ tree, hierarchy, divider: ' / ' }),
      status: orderStatus,
      trackingNumber,
      folderId,
      folderName: tree[folderId] ? tree[folderId].name : '',
      address: formattedAddress,
      accountNumber: clientAccountNumber,
      contactName,
      contactEmail,
      contactPhoneNumber,
      isCompleted: orderStatus === 'Delivered',
      entityType: 'orders' as const,
      orderType: orderType || 'Order',
      score,
      tenantId,
    }
  })
  return formattedOrders
}

const formatApiData = (
  tab: SearchTabsType,
  apiData: ISearchResultAccountApi[] | ISearchResultPersonApi[] | ISearchResultDeviceApi[] | ISearchResultOrderApi[],
  tree: IFolderTree
): SearchResultsType => {
  if (tab === 'accounts') return formatAccounts(apiData as ISearchResultAccountApi[], tree) as SearchResultsType
  if (tab === 'devices') return formatDevices(apiData as ISearchResultDeviceApi[], tree) as SearchResultsType
  if (tab === 'people') return formatPeople(apiData as ISearchResultPersonApi[]) as SearchResultsType
  return formatOrders(apiData as ISearchResultOrderApi[], tree) as SearchResultsType
}

export default formatApiData
